import "@/index.css";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProviderAuthProvider, useProviderAuth } from "@/contexts/ProviderAuthContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import UploadPage from "@/pages/UploadPage";
import RecordsPage from "@/pages/RecordsPage";
import FamilyPage from "@/pages/FamilyPage";
import RemindersPage from "@/pages/RemindersPage";
import DoctorNotesPage from "@/pages/DoctorNotesPage";
import PrescriptionsPage from "@/pages/PrescriptionsPage";
import CertificatesPage from "@/pages/CertificatesPage";
import ConsultationsPage from "@/pages/ConsultationsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AiScribePage from "@/pages/AiScribePage";
import SharedRecordPage from "@/pages/SharedRecordPage";
import ProviderLoginPage from "@/pages/ProviderLoginPage";
import ProviderDashboardPage from "@/pages/ProviderDashboardPage";
import PatientRequestsPage from "@/pages/PatientRequestsPage";
import NearbyHospitalsPage from "@/pages/NearbyHospitalsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import DailyHealthPage from "@/pages/DailyHealthPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading, token, refreshUser } = useAuth();

  useEffect(() => {
    if (!user && token && !loading) {
      refreshUser();
    }
  }, [user, token, loading, refreshUser]);
  
  if (loading || (token && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-loading-state">
        <div className="animate-pulse-soft text-blue-900 font-medium" data-testid="auth-loading-text">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="public-loading-state">
        <div className="animate-pulse-soft text-blue-900 font-medium" data-testid="public-loading-text">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const ProviderProtectedRoute = ({ children }) => {
  const { provider, loading, token } = useProviderAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="provider-loading-state">
        <div className="animate-pulse-soft text-blue-900 font-medium" data-testid="provider-loading-text">Loading...</div>
      </div>
    );
  }

  if (!token || !provider) {
    return <Navigate to="/provider/login" replace />;
  }

  return children;
};

const ProviderPublicRoute = ({ children }) => {
  const { provider, loading, token } = useProviderAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="provider-public-loading">
        <div className="animate-pulse-soft text-blue-900 font-medium">Loading...</div>
      </div>
    );
  }

  if (token && provider) {
    return <Navigate to="/provider/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ProviderAuthProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/share/:token" element={<SharedRecordPage />} />
            <Route path="/provider/login" element={<ProviderPublicRoute><ProviderLoginPage /></ProviderPublicRoute>} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
            <Route path="/records" element={<ProtectedRoute><RecordsPage /></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute><PatientRequestsPage /></ProtectedRoute>} />
            <Route path="/nearby-hospitals" element={<ProtectedRoute><NearbyHospitalsPage /></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
            <Route path="/daily-health" element={<ProtectedRoute><DailyHealthPage /></ProtectedRoute>} />
            <Route path="/family" element={<ProtectedRoute><FamilyPage /></ProtectedRoute>} />
            <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
            <Route path="/doctor-notes" element={<ProtectedRoute><DoctorNotesPage /></ProtectedRoute>} />
            <Route path="/prescriptions" element={<ProtectedRoute><PrescriptionsPage /></ProtectedRoute>} />
            <Route path="/certificates" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />
            <Route path="/consultations" element={<ProtectedRoute><ConsultationsPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/ai-scribe" element={<ProtectedRoute><AiScribePage /></ProtectedRoute>} />

            {/* Provider Routes */}
            <Route path="/provider/dashboard" element={<ProviderProtectedRoute><ProviderDashboardPage /></ProviderProtectedRoute>} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ProviderAuthProvider>
  );
}

export default App;
